import { Select, MenuItem, FormControl } from '@mui/material'
import { type SelectChangeEvent } from '@mui/material'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

import { HAMMERTIME_API_ERRORS } from '@/modules/api/hammer-time/lib/errors'

interface BirthdayPickerProps {
  readonly facebookErrors: string[] | null
  readonly birthdayDate: string
  readonly setBirthdayDate: (date: string) => void
  readonly signUpMutation: {
    isPending: boolean
  }
  readonly isDayOpen: boolean
  readonly setDayOpen: (open: boolean) => void
  readonly isMonthOpen: boolean
  readonly setMonthOpen: (open: boolean) => void
  readonly isYearOpen: boolean
  readonly setYearOpen: (open: boolean) => void
}

export function BirthdayPicker({
  facebookErrors,
  birthdayDate,
  setBirthdayDate,
  signUpMutation,
  isDayOpen,
  setDayOpen,
  isMonthOpen,
  setMonthOpen,
  isYearOpen,
  setYearOpen,
}: BirthdayPickerProps) {
  const days: number[] = Array.from({ length: 31 }, (_unused, index) => index + 1)

  const tMonth = useTranslations('auth.createAccount.months')
  const tTerms = useTranslations('auth.createAccount.date_terms')

  const months: Array<{ value: number; label: string }> = [
    { value: 1, label: tMonth('january') },
    { value: 2, label: tMonth('february') },
    { value: 3, label: tMonth('march') },
    { value: 4, label: tMonth('april') },
    { value: 5, label: tMonth('may') },
    { value: 6, label: tMonth('june') },
    { value: 7, label: tMonth('july') },
    { value: 8, label: tMonth('august') },
    { value: 9, label: tMonth('september') },
    { value: 10, label: tMonth('october') },
    { value: 11, label: tMonth('november') },
    { value: 12, label: tMonth('december') },
  ]

  const today = dayjs()
  const minYear = today.subtract(100, 'year').year()
  const maxYear = today.subtract(18, 'year').year()
  const years: number[] = Array.from({ length: maxYear - minYear + 1 }, (_unused, index) => maxYear - index)

  const [day, setDay] = useState<number>(0)
  const [month, setMonth] = useState<number>(0)
  const [year, setYear] = useState<number>(0)

  useEffect(() => {
    if (birthdayDate) {
      const [yearStr, monthStr, dayStr] = birthdayDate.split('-')
      const parsedYear = Number(yearStr)
      const parsedMonth = Number(monthStr)
      const parsedDay = Number(dayStr)

      setYear(parsedYear)
      setMonth(parsedMonth)

      const maxDay = dayjs(`${year}-${month}-01`).daysInMonth()

      if (day > maxDay) {
        setDay(maxDay)
      } else {
        setDay(parsedDay)
      }
    }
  }, [birthdayDate])

  useEffect(() => {
    function pad(num: number): string {
      return num < 10 ? `0${num}` : num.toString()
    }

    setBirthdayDate(`${year}-${pad(month)}-${pad(day)}`)
  }, [day, month, setBirthdayDate, year])

  if (facebookErrors && !facebookErrors.includes(HAMMERTIME_API_ERRORS.FACEBOOK.NO_BIRTHDAY)) {
    return null
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', width: '100%' }}>
      <FormControl sx={{ flex: 1 }}>
        <Select
          displayEmpty
          value={day}
          onChange={(event: SelectChangeEvent<number>) => setDay(Number(event.target.value))}
          size="small"
          disabled={signUpMutation.isPending}
          sx={{ textAlign: 'left' }}
          open={isDayOpen}
          onOpen={() => setDayOpen(true)}
          onClose={() => setDayOpen(false)}
        >
          <MenuItem disabled value="0">
            {tTerms('day')}
          </MenuItem>
          {days.map((day) => (
            <MenuItem key={day} value={day}>
              {day}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ flex: 1 }}>
        <Select
          displayEmpty
          value={month}
          onChange={(event: SelectChangeEvent<number>) => setMonth(Number(event.target.value))}
          size="small"
          disabled={signUpMutation.isPending}
          sx={{ textAlign: 'left' }}
          open={isMonthOpen}
          onOpen={() => setMonthOpen(true)}
          onClose={() => setMonthOpen(false)}
        >
          <MenuItem disabled value="0">
            {tTerms('month')}
          </MenuItem>
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ flex: 1 }}>
        <Select
          displayEmpty
          value={year}
          onChange={(event: SelectChangeEvent<number>) => setYear(Number(event.target.value))}
          size="small"
          disabled={signUpMutation.isPending}
          sx={{ textAlign: 'left' }}
          open={isYearOpen}
          onOpen={() => setYearOpen(true)}
          onClose={() => setYearOpen(false)}
        >
          <MenuItem disabled value="0">
            {tTerms('year')}
          </MenuItem>
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}
